// Utility function to detect and wrap placeholders in spans
export const wrapPlaceholders = (text: string): string => {
  // Match web format placeholders like {name} or {1, number}
  const webRegex = /\{(\w+)(?:,\s*(number|date|text|float))?\}/g;
  
  // Match unified format placeholders like [name] or [1:number]
  const unifiedRegex = /\[(\w+)(?::(?:number|date|text|float))?\]/g;
  
  // Match iOS format placeholders like %@ or %d or %i
  const iosRegex = /%[@dfi]/g;
  
  // Match Android format placeholders like %1$s or %1$d, including incomplete ones like %1$
  const androidRegex = /%\d+\$[sdf]?/g;
  
  // Match Lokalise format placeholders like [%name$s] or [%1$i]
  const lokaliseRegex = /\[%(?:\w+)\$[sif]\]/g;
  
  // Replace all placeholder formats with wrapped versions
  return text
    .replace(webRegex, '<span class="placeholder">$&</span>')
    .replace(unifiedRegex, '<span class="placeholder">$&</span>')
    .replace(iosRegex, '<span class="placeholder">$&</span>')
    .replace(androidRegex, '<span class="placeholder">$&</span>')
    .replace(lokaliseRegex, '<span class="placeholder">$&</span>');
};

// Function to safely set HTML content with placeholder highlighting
export const setContentWithHighlightedPlaceholders = (element: HTMLElement, text: string) => {
  // Only modify display when the element is not focused
  if (document.activeElement !== element) {
    // 1. Replace actual spaces with spans. Replace newlines with span + <br>.
    //    Visual symbols added via CSS ::before.
    const modifiedText = text
      .replace(/ /g, '<span class="whitespace space-char"> </span>') // Wrap space in span
      .replace(/\n/g, '<span class="whitespace newline-char"> </span><br>'); // Re-add newline span + <br>

    // 2. Wrap placeholders around the modified text structure
    const finalHTML = wrapPlaceholders(modifiedText);

    // 3. Set the final HTML. `white-space: pre-wrap` handles \n rendering.
    element.innerHTML = finalHTML;

  } else {
    // When focused, just set the plain text content
    element.textContent = text;
  }
};

// Function to extract placeholders from a string
export const extractPlaceholders = (text: string): string[] => {
  const placeholders: string[] = [];
  
  // Match web format placeholders like {name} or {1, number}
  const webRegex = /\{(\w+)(?:,\s*(number|date|text|float))?\}/g;
  
  // Match unified format placeholders like [name] or [1:number]
  const unifiedRegex = /\[(\w+)(?::(?:number|date|text|float))?\]/g;
  
  // Match iOS format placeholders like %@ or %d or %i
  const iosRegex = /%[@dfi]/g;
  
  // Match Android format placeholders like %1$s or %1$d, including incomplete ones like %1$
  const androidRegex = /%\d+\$[sdf]?/g;
  
  // Match Lokalise format placeholders like [%name$s] or [%1$i]
  const lokaliseRegex = /\[%(?:\w+)\$[sif]\]/g;
  
  // Extract all placeholders
  let match;
  while ((match = webRegex.exec(text)) !== null) {
    placeholders.push(match[0]);
  }
  while ((match = unifiedRegex.exec(text)) !== null) {
    placeholders.push(match[0]);
  }
  while ((match = iosRegex.exec(text)) !== null) {
    placeholders.push(match[0]);
  }
  while ((match = androidRegex.exec(text)) !== null) {
    placeholders.push(match[0]);
  }
  while ((match = lokaliseRegex.exec(text)) !== null) {
    placeholders.push(match[0]);
  }
  
  return placeholders;
}; 